// Automatic recovery from provider connection limits. A short, bounded retry
// budget lets a released connection drain without polling a rejected account
// indefinitely. Retry-After remains a minimum wait.

import { isStreamLimitStatus } from './playerErrors';

/** Floor for the wait — a `retryAfter: 0` must still let the slot drain. */
export const STREAM_LIMIT_RETRY_MIN_MS = 1000;
/** Ceiling for our own backoff; longer provider hints still take precedence. */
export const STREAM_LIMIT_RETRY_MAX_MS = 20000;
/** First progressive retry wait when the server sent no usable hint. */
export const STREAM_LIMIT_RETRY_DEFAULT_MS = 1000;
/** Increase the wait by two seconds after each consecutive rejection. */
export const STREAM_LIMIT_RETRY_STEP_MS = 2000;
/** Consecutive rejections across session acquisition and media attachment. */
export const STREAM_LIMIT_MAX_ATTEMPTS = 4;

export interface StreamLimitRetryPlan {
    retry: boolean;
    /** How long to wait before the next attempt. 0 when `retry` is false. */
    delayMs: number;
    /** The server's raw `retryAfter` (seconds), when it sent a usable one. */
    retryAfterSec: number | null;
}

const NO_RETRY: StreamLimitRetryPlan = { retry: false, delayMs: 0, retryAfterSec: null };

/**
 * The server's `retryAfter`, in seconds, or null when it sent nothing usable.
 * Reads the JSON body only — the `Retry-After` header is not exposed to the
 * fetch client, and the body carries the same number.
 */
export function streamLimitRetryAfterSec(err: unknown): number | null {
    if (!err || typeof err !== 'object') return null;
    const retryAfterSec = (err as { retryAfterSec?: unknown }).retryAfterSec;
    if (typeof retryAfterSec === 'number' && Number.isFinite(retryAfterSec) && retryAfterSec >= 0) return retryAfterSec;
    const body = (err as { body?: unknown }).body;
    if (!body || typeof body !== 'object') return null;
    const raw = (body as { retryAfter?: unknown }).retryAfter;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
}

/** True for a failed tune the provider rejected on its concurrent-stream cap. */
export function isStreamLimitError(err: unknown): boolean {
    if (!err || typeof err !== 'object') return false;
    // `code` is ApiError's getter over `body.code` / `body.error`.
    if ((err as { code?: unknown }).code === 'stream_limit') return true;
    const status = (err as { status?: unknown }).status;
    return typeof status === 'number' && isStreamLimitStatus(status);
}

/**
 * Decide whether a failed tune should be retried, and after how long.
 * `attemptsMade` counts attempts already spent, so the first failure passes 1.
 */
export function planStreamLimitRetry(err: unknown, attemptsMade: number): StreamLimitRetryPlan {
    if (!isStreamLimitError(err) || attemptsMade >= STREAM_LIMIT_MAX_ATTEMPTS) return NO_RETRY;
    const retryAfterSec = streamLimitRetryAfterSec(err);
    const requestedMs = retryAfterSec === null
        ? STREAM_LIMIT_RETRY_DEFAULT_MS
        : retryAfterSec * 1000;
    return {
        retry: true,
        // Cap our backoff, never shorten the provider's Retry-After. The
        // timer ceiling prevents setTimeout overflow from becoming a hot loop.
        delayMs: Math.min(2_147_483_647, Math.max(
            STREAM_LIMIT_RETRY_MIN_MS,
            requestedMs,
            Math.min(
                STREAM_LIMIT_RETRY_MAX_MS,
                STREAM_LIMIT_RETRY_DEFAULT_MS + STREAM_LIMIT_RETRY_STEP_MS * Math.max(0, attemptsMade - 1),
            ),
        )),
        retryAfterSec,
    };
}

export interface StreamLimitRetryContext {
    /** Rejections already seen by the same tune, including media requests. */
    attemptsMade?: number;
    /** Start with the limit already reported by the media player. */
    initialError?: unknown;
    /** Cancels the wait AND the retry — a newer tune supersedes this one. */
    signal?: AbortSignal;
    /**
     * Second cancellation source for state a signal cannot express (the
     * store's in-flight tune key). Returning false stops the retry.
     */
    stillCurrent?: () => boolean;
    /** Observability hook — fired once per scheduled retry, before the wait. */
    onRetry?: (info: { attempt: number; delayMs: number; retryAfterSec: number | null }) => void;
}

function abortError(): Error {
    // Named so callers' existing `err.name === 'AbortError'` branches treat a
    // superseded retry exactly like a superseded request.
    const err = new Error('Stream-limit retry aborted');
    err.name = 'AbortError';
    return err;
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
        if (signal?.aborted) {
            reject(abortError());
            return;
        }
        const timer = setTimeout(() => {
            signal?.removeEventListener('abort', onAbort);
            resolve();
        }, ms);
        function onAbort(): void {
            clearTimeout(timer);
            reject(abortError());
        }
        signal?.addEventListener('abort', onAbort, { once: true });
    });
}

/**
 * Retry connection limits within the budget while the tune is current.
 * Callers supply cancellation so Stop and a newer tune end recovery immediately.
 */
export async function resolveWithStreamLimitRetry<T>(
    resolve: () => Promise<T>,
    ctx: StreamLimitRetryContext = {},
): Promise<T> {
    let attemptsMade = ctx.attemptsMade ?? 0;
    let initialError = ctx.initialError;
    for (;;) {
        if (ctx.signal?.aborted || ctx.stillCurrent?.() === false) throw abortError();
        try {
            attemptsMade += 1;
            if (initialError) {
                const error = initialError;
                initialError = undefined;
                throw error;
            }
            return await resolve();
        } catch (err) {
            const plan = planStreamLimitRetry(err, attemptsMade);
            if (!plan.retry) throw err;
            if (ctx.signal?.aborted || ctx.stillCurrent?.() === false) throw abortError();
            ctx.onRetry?.({
                attempt: attemptsMade + 1,
                delayMs: plan.delayMs,
                retryAfterSec: plan.retryAfterSec,
            });
            await sleep(plan.delayMs, ctx.signal);
            // The user moved on while we waited — surfacing the stale limit
            // error would toast about a channel they already left.
            if (ctx.stillCurrent?.() === false) throw abortError();
        }
    }
}
